import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { UserService } from '../user/user.service';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';

/** Read token without injecting AuthService to avoid NG0200 circular dependency */
function getAccessToken(): string {
  return localStorage.getItem('accessToken') ?? '';
}

/** Sign out without injecting AuthService to avoid NG0200 */
function doSignOut(injector: Injector): void {
  localStorage.removeItem('accessToken');
  try {
    injector.get(UserService).clearUserData();
    injector.get(Router).navigate(['/auth/sign-in']);
  } catch {
    window.location.href = '/auth/sign-in';
  }
}

/** Refresh token via fetch (bypasses HttpClient) to avoid NG0200 circular dependency */
async function refreshTokenViaFetch(): Promise<{ success: boolean; accessToken?: string; user?: any }> {
  const token = getAccessToken();
  if (!token) return { success: false };
  try {
    const res = await fetch(`${environment.apiUrl}/auth/sign-in-with-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken: token }),
    });
    if (!res.ok) return { success: false };
    const data = await res.json();
    if (data.accessToken) localStorage.setItem('accessToken', data.accessToken);
    return { success: true, accessToken: data.accessToken, user: data.user };
  } catch {
    return { success: false };
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);

  // Skip adding auth headers for public auth endpoints
  if (
    req.url.includes('/auth/login')
    || req.url.includes('/auth/sign-up')
    || req.url.includes('/auth/sign-in-with-token')
    || req.url.includes('/auth/forgot-password')
    || req.url.includes('/auth/forgot-password-sms')
    || req.url.includes('/auth/reset-password')
    || req.url.includes('/auth/validate-reset-token')
    || req.url.includes('/auth/send-otp')
    || req.url.includes('/auth/verify-otp')
    || req.url.includes('/auth/direct-login')
    || req.url.includes('/auth/check-user-exists')
  ) {
    return next(req);
  }

  const token = getAccessToken();
  if (token) {
    let tenantId = '';
    let storeId = '';
    if (!req.url.includes('/auth/get')) {
      try {
        const userService = injector.get(UserService);
        const user = userService.currentUser;
        tenantId = user?.tenants?.[0]?.id || '';
        const isOnboardingRoute =
          typeof window !== 'undefined' &&
          window.location.pathname.includes('/on-boarding/sign-up-onboarding');
        const onboardingIncomplete = user?.onboardingComplete === false;
        if (!isOnboardingRoute && !onboardingIncomplete) {
          const activeStoreId =
            typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('active_store_id') : null;
          storeId = (activeStoreId && activeStoreId.trim()) || user?.stores?.[0]?.id || '';
        }
      } catch { /* avoid cycle on bootstrap */ }
    }

    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'tenant-id': tenantId,
        'store-id': storeId
      }
    });

    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return from(refreshTokenViaFetch()).pipe(
            switchMap((result) => {
              if (result.success && result.accessToken && result.user) {
                try {
                  injector.get(UserService).user = result.user;
                } catch { /* UserService may cause cycle during bootstrap */ }
                const tid = result.user?.tenants?.[0]?.id || '';
                const isOnboardingRoute =
                  typeof window !== 'undefined' &&
                  window.location.pathname.includes('/on-boarding/sign-up-onboarding');
                const onboardingIncomplete = result.user?.onboardingComplete === false;
                let sid = '';
                if (!isOnboardingRoute && !onboardingIncomplete) {
                  const activeStoreId =
                    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('active_store_id') : null;
                  sid = (activeStoreId && activeStoreId.trim()) || result.user?.stores?.[0]?.id || '';
                }
                const retryReq = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${result.accessToken}`,
                    'tenant-id': tid,
                    'store-id': sid
                  }
                });
                return next(retryReq);
              }
              doSignOut(injector);
              return throwError(() => error);
            }),
            catchError(() => {
              doSignOut(injector);
              return throwError(() => error);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  return next(req);
};